import { debounce } from 'lodash';
import { Form } from '@ant-design/compatible';
import { Input, InputNumber, AutoComplete, Radio, Select, Icon, Checkbox } from 'antd';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Row, Col } from 'common/components/grid';
import hexToRGBA from 'common/utils/hex-to-rgba';
import theme from 'common/theme';
import classNames from 'classnames';
import Typography from 'common/components/typography';
import event from '~helpers/mixpanel';
import { QUERY_GET_CITIES, QUERY_GET_COUNTRIES } from '../queries';
import OnboardingContext from '../context';
const { TextArea } = Input;
const { Option } = AutoComplete;

const useStyles = makeStyles(theme => ({
  businessFormWrapper: {
    background: theme.colors.gray1,
    textAlign: 'left',

    '& .ant-legacy-form-item-label': {
      width: '100% !important',
      textAlign: 'left !important',
      color: hexToRGBA(theme.colors.gray10, 0.85),
      fontWeight: 600,
      fontSize: 14,
      lineHeight: '22px',
      marginBottom: 6,

      '& label': {
        display: 'inline-block',
        width: '100%',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      },
    },
    '& .ant-radio-wrapper-checked': {
      borderColor: '#38B2A6',
    },
  },
  businessContent: {
    display: 'block',
    marginBottom: 24,
    textAlign: 'left',
  },
  registerAddress: {
    marginTop: 0,
  },
  website: {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '22px',
    color: 'rgba(0, 0, 0, 0.85)',
    marginBottom: 6,
  },
  option: {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '22px',
    color: 'rgba(0, 0, 0, 0.45)',
  },
  registerWrapper: {
    background: hexToRGBA(theme.colors.blue1, 0.6),
    borderRadius: 2,
    padding: '20px 20px 2px',
    marginBottom: 24,

    '& .ant-legacy-form-item-label label': {
      fontSize: 12,
      lineHeight: '20px',
    },
  },
  summary: {
    display: 'block',
    marginBottom: 24,
  },
  name: {
    width: 230,
    margin: 0,
  },
  radioBox: {
    border: '1px solid #D9D9D9',
    margin: 0,
    width: 230,
    padding: '9px',
    transition: 'all 0.3s @ease-in-out',
    userSelect: 'none',
    '&:hover': {
      borderColor: '#5abfb2',
    },
    '&:first-child': {
      marginRight: 25,
    },
  },
  icon: {
    marginRight: 6,
    '& svg': {
      color: theme.colors.disableColor,
      width: 12,
      height: 12,
    },
  },
  hint: {
    fontWeight: 400,
    ...theme.typography.body4,
    color: theme.colors.disableColor,
  },
  checkBox: {
    display: 'flex',
    alignItems: 'baseline',
  },
  checkedbottom: {
    ...theme.typography.body2,
    fontWeight: 400,
    marginLeft: 8,
  },
  formCheckboxError: {
    background: '#FFF1F0!important',
    '& .ant-checkbox-inner': { border: '1px solid #F5222D' },
  },
  formCheckbox: {
    padding: '24px 40px',
    margin: '-24px -40px',
    marginTop: 32,
    background: theme.colors.gray2,
    color: theme.colors.bodyColor,
    '& .ant-legacy-form-explain': {
      ...theme.typography.body2,
      color: theme.colors.red11,
      marginLeft: 24,
      marginTop: 4,
    },
    '& .ant-checkbox-wrapper': {
      lineHeight: '22px',
    },
  },
}));
const companyTypeArr = [
  'REAL_ESTATE_AGENCY',
  'PROPERTY_MANAGEMENT_COMPANY',
  'PRIVATE_MANAGEMENT',
  'UNIVERSITY_OWNED_OR_MANAGED',
  'HMO',
  'OTHER',
];
const BusinessForm = props => {
  const { lastName, firstName } = useSelector(state => {
    return {
      firstName: state?.auth?.getIn(['base', 'firstName']),
      lastName: state?.auth?.getIn(['base', 'lastName']),
    };
  });
  const [businessType, setBussinesType] = useState('COMPANY');
  const [individualCheckedError, setIndividualCheckedError] = useState(false);
  const onboardingData = useContext(OnboardingContext);
  const { setBusinessFormRef, mutationCompleteOnboarding, landlordId, propertyTypeSlug } =
    onboardingData;
  const [cityId, setCityId] = useState(null);
  const classes = useStyles();
  const businessFormRef = useRef(null);
  const { t } = useTranslation();
  const { getFieldDecorator, getFieldValue, validateFields, setFields, resetFields } = props.form;

  const [getCities, { data: cities }] = useLazyQuery(QUERY_GET_CITIES, {});
  const { data: countries } = useQuery(QUERY_GET_COUNTRIES, {
    variables: {
      pageSize: 50,
    },
  });

  useEffect(() => {
    if (businessFormRef) {
      setBusinessFormRef(businessFormRef);
    }
  }, [businessFormRef, setBusinessFormRef]);

  const eventTracking = type => {
    event('ClickedOP Business Info Page', {
      'Clicked Position': type,
      'Property Type': propertyTypeSlug,
    });
  };

  const renderCityOptions = useMemo(() => {
    const list = cities?.cities?.edges;
    if (list && list.length > 0) {
      return list.map(city => <Option key={city?.node?.id}>{city?.node?.name}</Option>);
    } else {
      return [];
    }
  }, [cities]);

  const renderCountryOptions = useMemo(() => {
    const list = countries?.countries?.edges;
    if (list && list.length > 0) {
      return list.map(country => <Option key={country?.node?.slug}>{country?.node?.name}</Option>);
    } else {
      return [];
    }
  }, [countries]);

  const handleSearchCity = value => {
    if (value?.trim()) {
      setCityId(null);
      const countrySlug = getFieldValue('country');
      getCities({
        variables: {
          nameLike: value?.trim(),
          countrySlugs: countrySlug || undefined,
        },
      });
    }
  };

  const handleSubmit = () => {
    validateFields((err, values) => {
      if (!cityId) {
        setFields({
          cityId: {
            value: values.cityId,
            errors: [new Error(t('landlord.onboarding.business.empty_error.city'))],
          },
        });
      }

      if (!err) {
        if (businessType === 'COMPANY') {
          mutationCompleteOnboarding({
            variables: {
              input: {
                businessType: values.businessType,
                businessScale: values.businessScale,
                landlordWebsite: values.landlordWebsite,
                companyType: values.companyType,
                billingAddress: values?.address,
                billingCityId: values?.cityId,
                businessNumber: values?.businessNumber,
                businessScale: values?.businessScale,
                id: landlordId,
                name: values?.name,
              },
            },
          });
        } else {
          mutationCompleteOnboarding({
            variables: {
              input: {
                businessType: values.businessType,
                businessScale: values.businessScale,
                landlordWebsite: values.landlordWebsite,
                firstName: values.firstName,
                lastName: values.lastName,
                governmentIssuedId: values.governmentIssuedId,
                billingAddress: values?.address,
                billingCityId: values?.cityId,
                businessScale: values?.businessScale,
                id: landlordId,
              },
            },
          });
        }
      }
    });
  };

  const handleSelectCity = value => {
    setCityId(value);
  };

  return (
    <Form
      className={classes.businessFormWrapper}
      hideRequiredMark
      ref={businessFormRef}
      onSubmit={handleSubmit}
    >
      <Typography
        themeColor={hexToRGBA(theme.colors.gray10, 0.65)}
        fontSize={14}
        className={classes.summary}
      >
        {t('landlord.onboarding.business.form_summary')}
      </Typography>

      <Form.Item label={t('landlord.onboarding.label.business_type')} colon={false}>
        {getFieldDecorator('businessType', {
          initialValue: 'COMPANY',
          rules: [
            {
              required: true,
              message: t('landlord.onboarding.business.empty_error.company_name'),
            },
          ],
          trigger: 'onChange',
        })(
          <Radio.Group
            onChange={e => {
              setBussinesType(e.target.value);
              resetFields();
            }}
            name="radiogroup"
          >
            <Radio className={classes.radioBox} value="COMPANY">
              {t('landlord.onboarding.selcet.business_typ.company')}
            </Radio>
            <Radio className={classes.radioBox} value="INDIVIDUAL">
              {t('landlord.onboarding.selcet.business_typ.individual')}
            </Radio>
          </Radio.Group>,
        )}
      </Form.Item>
      {/* ---- */}
      <If condition={businessType === 'COMPANY'}>
        <Form.Item label={t('landlord.onboarding.label.company_type')}>
          {getFieldDecorator('companyType', {
            rules: [
              {
                required: true,
                message: t('landlord.onboarding.business.company_type.error'),
              },
            ],
          })(
            <Select
              size="large"
              style={{ width: 485 }}
              onChange={() => {}}
              placeholder={t('landlord.onboarding.select.company_type.placeHolder')}
            >
              <For each="companyType" of={companyTypeArr} index="index">
                <Select.Option value={companyType} key={index}>
                  {t(`landlord.onboarding.select.company_type.${companyType?.toLowerCase()}`)}
                </Select.Option>
              </For>
            </Select>,
          )}
        </Form.Item>
        <Form.Item label={t('landlord.onboarding.business.label.company_name')} colon={false}>
          {getFieldDecorator('name', {
            rules: [
              {
                required: true,
                message: t('landlord.onboarding.business.empty_error.company_name'),
                whitespace: true,
              },
            ],
          })(
            <Input
              placeholder={t('landlord.onboarding.business.placeholder.company_name')}
              size="large"
              onClick={() => {
                eventTracking('Company Name');
              }}
            />,
          )}
        </Form.Item>
        <Form.Item label={t('landlord.onboarding.business.label.business_number')} colon={false}>
          {getFieldDecorator('businessNumber', {
            rules: [
              {
                required: true,
                message: t('landlord.onboarding.business.empty_error.business_number'),
                whitespace: true,
              },
            ],
          })(
            <Input
              placeholder={t('landlord.onboarding.business.placeholder.business_number')}
              size="large"
              onClick={() => {
                eventTracking('Business Number');
              }}
            />,
          )}
        </Form.Item>
        <Typography
          textType={'title'}
          type="h4"
          fontSize={14}
          lineHeight={22}
          themeColor={hexToRGBA(theme.colors.gray10, 0.85)}
          className={classes.registerAddress}
        >
          {t('landlord.onboarding.business.title.registered_address')}
        </Typography>
        <div className={classes.registerWrapper}>
          <Row gutter={{ xs: 24, sm: 24, md: 24 }}>
            <Col span={12}>
              <Form.Item label={t('landlord.onboarding.business.label.country')} colon={false}>
                {getFieldDecorator('country', {
                  rules: [
                    {
                      required: true,
                      message: t('landlord.onboarding.business.empty_error.country'),
                      whitespace: true,
                    },
                  ],
                })(
                  <AutoComplete
                    placeholder={t('landlord.onboarding.business.placeholder.country')}
                    size="large"
                    dataSource={renderCountryOptions}
                  >
                    <Input
                      size="large"
                      onClick={() => {
                        eventTracking('Country');
                      }}
                    />
                  </AutoComplete>,
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('landlord.onboarding.business.label.city')} colon={false}>
                {getFieldDecorator('cityId', {
                  rules: [
                    {
                      required: true,
                      message: t('landlord.onboarding.business.empty_error.city'),
                      whitespace: true,
                    },
                  ],
                })(
                  <AutoComplete
                    placeholder={t('landlord.onboarding.business.placeholder.city')}
                    size="large"
                    dataSource={renderCityOptions}
                    onSearch={debounce(handleSearchCity, 300)}
                    onSelect={handleSelectCity}
                  >
                    <Input
                      size="large"
                      onClick={() => {
                        eventTracking('City');
                      }}
                    />
                  </AutoComplete>,
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item label={t('landlord.onboarding.business.label.street')} colon={false}>
                {getFieldDecorator('address', {
                  rules: [
                    {
                      required: true,
                      message: t('landlord.onboarding.business.empty_error.street'),
                      whitespace: true,
                    },
                  ],
                })(
                  <Input
                    placeholder={t('landlord.onboarding.business.placeholder.street')}
                    size="large"
                    onClick={() => {
                      eventTracking('Street');
                    }}
                  />,
                )}
              </Form.Item>
            </Col>
          </Row>
        </div>
      </If>
      <If condition={businessType === 'INDIVIDUAL'}>
        <Row gutter={{ xs: 24, sm: 24, md: 24 }}>
          <Col span={12}>
            <Form.Item label={t('landlord.onboarding.label.first_name')} colon={false}>
              {getFieldDecorator('firstName', {
                initialValue: firstName || null,
                rules: [
                  {
                    required: true,

                    message: t('landlord.onboarding.first_name.error'),
                    whitespace: true,
                  },
                  {
                    pattern: new RegExp(/^[a-zA-Z@~`!@#$%^&*()_=+\\\\';:\"\\/?>.<,-]+$/i),
                    message: t('landlord.onboarding.error.only_enlish_character'),
                  },
                ],
              })(
                <Input
                  placeholder={t('landlord.onboarding.label.first_name')}
                  size="large"
                  onClick={() => {
                    eventTracking('FirstName');
                  }}
                />,
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('landlord.onboarding.label.last_name')} colon={false}>
              {getFieldDecorator('lastName', {
                initialValue: lastName || null,
                rules: [
                  {
                    required: true,
                    message: t('landlord.onboarding.last_name.error'),
                    whitespace: true,
                  },
                  {
                    pattern: new RegExp(/^[a-zA-Z@~`!@#$%^&*()_=+\\\\';:\"\\/?>.<,-]+$/i),
                    message: t('landlord.onboarding.error.only_enlish_character'),
                  },
                ],
              })(
                <Input
                  placeholder={t('landlord.onboarding.label.last_name')}
                  size="large"
                  onClick={() => {
                    eventTracking('LastName');
                  }}
                />,
              )}
            </Form.Item>
          </Col>
        </Row>
        <Typography
          textType={'title'}
          type="h4"
          fontSize={14}
          lineHeight={22}
          themeColor={hexToRGBA(theme.colors.gray10, 0.85)}
          className={classes.registerAddress}
        >
          {t('landlord.onboarding.business.title.home_address')}
        </Typography>
        <div className={classes.registerWrapper}>
          <Row gutter={{ xs: 24, sm: 24, md: 24 }}>
            <Col span={12}>
              <Form.Item label={t('landlord.onboarding.business.label.country')} colon={false}>
                {getFieldDecorator('country', {
                  rules: [
                    {
                      required: true,
                      message: t('landlord.onboarding.business.empty_error.country'),
                      whitespace: true,
                    },
                  ],
                })(
                  <AutoComplete
                    placeholder={t('landlord.onboarding.business.placeholder.country')}
                    size="large"
                    dataSource={renderCountryOptions}
                  >
                    <Input
                      size="large"
                      onClick={() => {
                        eventTracking('Country');
                      }}
                    />
                  </AutoComplete>,
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('landlord.onboarding.business.label.city')} colon={false}>
                {getFieldDecorator('cityId', {
                  rules: [
                    {
                      required: true,
                      message: t('landlord.onboarding.business.empty_error.city'),
                      whitespace: true,
                    },
                  ],
                })(
                  <AutoComplete
                    placeholder={t('landlord.onboarding.business.placeholder.city')}
                    size="large"
                    dataSource={renderCityOptions}
                    onSearch={debounce(handleSearchCity, 300)}
                    onSelect={handleSelectCity}
                  >
                    <Input
                      size="large"
                      onClick={() => {
                        eventTracking('City');
                      }}
                    />
                  </AutoComplete>,
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                label={t('landlord.withdrawal.create.bank_transfer.address')}
                colon={false}
              >
                {getFieldDecorator('address', {
                  rules: [
                    {
                      required: true,
                      message: t('landlord.onboarding.business.empty_error.street'),
                      whitespace: true,
                    },
                  ],
                })(
                  <Input
                    placeholder={t('landlord.onboarding.business.placeholder.street')}
                    size="large"
                    onClick={() => {
                      eventTracking('Street');
                    }}
                  />,
                )}
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Form.Item label={t('landlord.onboarding.label.government_issued_id')}>
          {getFieldDecorator('governmentIssuedId', {
            rules: [
              {
                required: true,
                message: t('landlord.onboarding.government_issued_id.empty.error'),
              },
              {
                max: 250,
                message: t('landlord.onboarding.government_issued_id.empty.max_characters'),
              },
              {
                pattern: /^[^\s]*$/,
                message: t('landlord.onboarding.government_issued_id.empty.no_space'),
              },
            ],
          })(
            <div>
              <Input
                placeholder={t('landlord.onboarding.government_issued_id.placeholder')}
                size="large"
              />
              {/* <Input /> */}
              <div style={{ lineHeight: '22px' }}>
                <Icon className={classes.icon} type="info-circle" />
                <span className={classes.hint}>
                  {t('landlord.onboarding.government_issued_id.hint')}
                </span>
              </div>
            </div>,
          )}
        </Form.Item>
      </If>
      {/* end */}
      <Form.Item label={t('landlord.onboarding.business.label.property_quantity')} colon={false}>
        {getFieldDecorator('businessScale', {
          rules: [
            {
              required: true,
              message: t('landlord.onboarding.business.empty_error.property_quantity'),
              whitespace: true,
              type: 'integer',
            },
            {
              validator: (rule, value, callback) => {
                if (value <= 0 || value > 999) {
                  callback(
                    t('landlord.onboarding.business.empty_error.property_quantity_error_range'),
                  );
                } else {
                  callback();
                }
              },
            },
          ],
        })(
          <InputNumber
            placeholder={t('landlord.onboarding.business.placeholder.property_quantity')}
            size="large"
            onClick={() => {
              eventTracking('Business Scale');
            }}
          />,
        )}
      </Form.Item>
      <Form.Item>
        <div className={classes.website}>
          {t('landlord.onboarding.label.landlord_website')}
          <span className={classes.option}>
            {t('landlord.payment.first_instalment.title.optional')}
          </span>
        </div>

        {getFieldDecorator('landlordWebsite', {
          rules: [
            {
              whitespace: true,
              message: t('landlord.onboarding.landlord_website.error'),
              type: 'url',
            },
          ],
        })(
          <Input
            size="large"
            placeholder={t('landlord.onboarding.landlord_website.placeholder')}
          />,
        )}
      </Form.Item>
      <If condition={businessType === 'INDIVIDUAL'}>
        <Form.Item
          className={classNames(classes.formCheckbox, {
            [classes.formCheckboxError]: individualCheckedError,
          })}
        >
          {getFieldDecorator('individualChecked', {
            rules: [
              {
                validator: (rule, value, callback) => {
                  value === true;
                  if (!value) {
                    setIndividualCheckedError(true);
                    callback(t('landlord.onboarding.individual.confirm.error.hint'));
                  } else {
                    callback();
                  }
                },
              },
            ],
          })(
            <div className={classes.checkBox}>
              <Checkbox
                onChange={e => {
                  setIndividualCheckedError(!e.target.checked);
                }}
              />
              <span className={classes.checkedbottom}>
                {t('landlord.onboarding.individual.confirm.text')}
              </span>
            </div>,
          )}
        </Form.Item>
      </If>
    </Form>
  );
};

export default Form.create({
  name: 'business_form',
})(BusinessForm);

BusinessForm.propTypes = {
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func,
    setFields: PropTypes.func,
    getFieldValue: PropTypes.func,
    validateFields: PropTypes.func,
  }).isRequired,
};
